import { useCallback, useEffect, useState } from 'react';
import type { ListaSugerencias, Sugerencia } from '../../../api/ops/clientesDuplicados';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Deshacer } from '../../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { rolesDe } from '../../../logica/acceso';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { estadoDeError } from '../../../logica/estados';
import { cuando } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import { textoDeError } from '../../equipo/textos';
import type { PropsPantalla } from '../../tipos';
import { Comparar, type Apertura } from './Comparar';
import { AYUDA, FILTROS, MOTIVO, PERMISOS_DUPLICADOS, TEXTOS, VACIA } from './textos';
import '../../../componentes/apartado.css';
import './duplicados.css';

/**
 * Duplicados (2.5): sugerencias con los dos clientes (G148) y lo que coincide, filtradas por Por revisar · Fusionadas ·
 * Descartadas sin cifras (G140). Comparar y fusionar en RV Panel; fusionar va con ventana y no se deshace (G160);
 * «Son personas distintas» se aplica al momento con «Deshacer» 5 s: la petición sale al acabar (G159). Fusionan el
 * propietario y el administrador (`clientes.duplicados.fusionar`); el encargado solo ve (G161).
 */
export function Duplicados(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const ver = permisos.puede(PERMISOS_DUPLICADOS.ver);
    const fusionar = permisos.puede(PERMISOS_DUPLICADOS.fusionar) && !soloLectura;

    const [filtro, setFiltro] = useState(0);
    const estado = FILTROS[filtro]?.estado ?? 'open';
    const pag = usePaginacionCursor();
    // Lo cargado, con la vista (filtro y página) a la que corresponde: al cambiar de vista se ve «Cargando…».
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaSugerencias | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<Apertura | null>(null);
    const [hecho, setHecho] = useState<string | null>(null);
    const envio = useEnvioRetrasado();
    const { reiniciar } = pag;

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        const vista = `${estado}|${pag.cursor ?? ''}`;
        ops.listDuplicateSuggestions(negocio, estado, { cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, estado, pag.cursor, intento, ver]);

    const vistaActual = `${estado}|${pag.cursor ?? ''}`;
    const lista = cargada?.vista === vistaActual ? cargada.lista : null;
    const error = cargada?.vista === vistaActual ? cargada.error : null;

    const acciones: AccionCabecera[] = [];
    if (ver && permisos.puede(PERMISOS_DUPLICADOS.fusionar)) {
        acciones.push({ clave: 'fusionar', texto: 'Fusionar dos clientes', icono: 'git-merge', desactivado: soloLectura, onClick: () => { setHecho(null); setAbierta({ tipo: 'manual' }); } });
    }
    useCabecera({ ayuda: ver ? AYUDA.lista : [], acciones });

    if (!ver) {
        return <EstadoSinPermiso seccion="Duplicados" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    // «Son personas distintas»: se ve al momento y la petición sale a los 5 s, salvo «Deshacer» (G159).
    const descartar = (s: Sugerencia) => {
        setAbierta(null);
        envio.programar({
            texto: TEXTOS.noSeVuelven,
            donde: s.id,
            enviar: () => {
                ops.dismissDuplicateSuggestion(negocio, s.id)
                    .then(recargar)
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido descartar la sugerencia. Prueba de nuevo.') });
                        recargar();
                    });
            },
        });
    };

    const cambiarFiltro = (i: number) => {
        setFiltro(i);
        setHecho(null);
        reiniciar();
    };

    const contenido = (() => {
        if (error !== null && lista === null) {
            return <EstadoError seccion="los duplicados" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
        }
        if (lista === null) {
            return <EstadoCargando seccion="duplicados" />;
        }
        if (lista.data.length === 0) {
            return (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{VACIA[estado].titulo}</strong>
                    <span>{VACIA[estado].texto}</span>
                </div>
            );
        }
        const ahora = new Date();
        return (
            <>
                {!fusionar && estado === 'open' && (
                    <p className="rv-ca-nota">
                        <Icono nombre="eye" />
                        <span>{soloLectura ? TEXTOS.soloVerNegocio : TEXTOS.soloVerRol}</span>
                    </p>
                )}
                <div className="rv-ca-lista" role="list" aria-label="Sugerencias de duplicados">
                    {lista.data.map((s) => {
                        const [icono, motivo] = MOTIVO[s.reason];
                        const [a, b] = s.customers ?? [];
                        const nombreA = a?.display_name ?? '';
                        const nombreB = b?.display_name ?? '';
                        const pendiente = envio.pendiente?.donde === s.id;
                        return (
                            <div key={s.id} role="listitem" className={`rv-ca-fila-caja${pendiente ? ' rv-ca-fila-caja--apagada' : ''}`}>
                                <div
                                    className="rv-ca-fila rv-ca-fila--abre"
                                    tabIndex={pendiente ? -1 : 0}
                                    aria-label={`${nombreA} y ${nombreB}: ${motivo}. Comparar`}
                                    onClick={() => {
                                        if (!pendiente) {
                                            setAbierta({ tipo: 'sugerencia', sugerencia: s });
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !pendiente) {
                                            setAbierta({ tipo: 'sugerencia', sugerencia: s });
                                        }
                                    }}
                                >
                                    <span className="rv-ca-baldosa">
                                        <Icono nombre={icono} />
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <strong>
                                            <span>{nombreA}</span>
                                            <span className="rv-ca-y">y</span>
                                            <span>{nombreB}</span>
                                        </strong>
                                        <span>{[motivo, cuando(s.created_at, ahora)].filter((t) => t !== '').join(' · ')}</span>
                                    </span>
                                    {s.status === 'open' && fusionar && !pendiente && (
                                        <button
                                            type="button"
                                            className="rv-bi rv-ca-fantasma"
                                            aria-label={`Son personas distintas: ${nombreA} y ${nombreB}`}
                                            title={`Son personas distintas: ${nombreA} y ${nombreB}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                descartar(s);
                                            }}
                                        >
                                            <Icono nombre="users" />
                                        </button>
                                    )}
                                    <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                </div>
                                {pendiente && envio.pendiente !== null && (
                                    <div className="rv-ca-deshacer">
                                        <span>{envio.pendiente.texto}</span>
                                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            </>
        );
    })();

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Estado de las sugerencias" items={FILTROS} valor={filtro} onCambio={cambiarFiltro} />
            {hecho !== null && estado === 'open' && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {contenido}
            <Comparar
                ops={ops}
                negocio={negocio}
                movil={movil}
                apertura={abierta}
                puedeFusionar={fusionar}
                soloLectura={soloLectura}
                onCerrar={() => {
                    setAbierta(null);
                }}
                onDescartar={descartar}
                onFusionada={(texto) => {
                    setAbierta(null);
                    setHecho(texto);
                    recargar();
                }}
            />
        </div>
    );
}
